import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waitlist");
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("nf_admin_token");
        navigate("/admin/login");
        return;
      }
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not load waitlist.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("nf_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setAdminEmail(localStorage.getItem("nf_admin_email") || "");
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("nf_admin_token");
    localStorage.removeItem("nf_admin_email");
    navigate("/admin/login");
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("nf_admin_token");
      const res = await fetch(`${API_BASE}/admin/waitlist/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nourished-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error("Could not export CSV.");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Remove this entry from the waitlist?")) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Entry removed.");
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFCEF", color: "#1A1A1A" }}>
      {/* HEADER */}
      <header className="border-b border-[#E5D9B0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-display italic text-2xl">Nourished</span>
            <span className="font-display text-2xl" style={{ color: "#800606" }}>Fertility</span>
            <span className="meta-label mt-1">ADMIN PANEL</span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="meta-label hidden md:inline" data-testid="admin-email-display">{adminEmail}</span>
            <Link to="/" className="meta-label hover:text-[#800606] transition-colors">View Site</Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="meta-label hover:text-[#800606] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* TITLE */}
      <section className="border-b border-[#E5D9B0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="meta-label">VOLUME I &middot; INDEX OF SOULS</p>
            <h1 className="font-display text-[48px] md:text-[72px] leading-[1.0] mt-4">
              The <em style={{ color: "#800606" }}>Waitlist.</em>
            </h1>
            <p className="mt-6 text-[18px] text-[#5A5A5A] max-w-xl">
              Every woman who has asked to be among the first to know.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <div className="font-display text-[64px] md:text-[80px] leading-none" data-testid="waitlist-count">
              {loading ? "—" : items.length.toString().padStart(2, "0")}
            </div>
            <p className="meta-label mt-2">TOTAL ENTRIES</p>
            <button
              data-testid="export-csv-button"
              onClick={downloadCsv}
              disabled={loading || items.length === 0}
              className="mt-6 inline-flex items-center gap-3 px-6 py-3 font-mono-cutive text-[11px] tracking-[0.2em] uppercase border border-[#1A1A1A] disabled:opacity-40"
              style={{ backgroundColor: "transparent", color: "#1A1A1A" }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = "#1A1A1A"; e.currentTarget.style.color = "#FFFCEF"; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#1A1A1A"; }}
            >
              Export CSV ↓
            </button>
          </div>
        </div>
      </section>

      {/* TABLE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          {loading ? (
            <p className="meta-label" data-testid="loading-state">LOADING…</p>
          ) : items.length === 0 ? (
            <div className="border border-[#E5D9B0] p-16 text-center" data-testid="empty-state">
              <p className="meta-label">NO ENTRIES YET</p>
              <p className="font-display italic text-[24px] mt-4 text-[#5A5A5A]">
                When she's ready, she will come.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="waitlist-table">
                <thead>
                  <tr className="border-y border-[#1A1A1A]">
                    <th className="meta-label text-left py-4 pr-4 w-12">NO.</th>
                    <th className="meta-label text-left py-4 pr-4">NAME</th>
                    <th className="meta-label text-left py-4 pr-4">EMAIL</th>
                    <th className="meta-label text-left py-4 pr-4">PRIMARY FOCUS</th>
                    <th className="meta-label text-left py-4 pr-4">JOINED</th>
                    <th className="meta-label text-left py-4 pr-4">EMAIL</th>
                    <th className="meta-label text-right py-4 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.id} className="border-b border-[#E5D9B0]" data-testid={`waitlist-row-${i}`}>
                      <td className="font-mono-cutive text-[14px] py-5 pr-4 text-[#5A5A5A]">
                        {(items.length - i).toString().padStart(3, "0")}
                      </td>
                      <td className="font-display text-[20px] py-5 pr-4">{it.name}</td>
                      <td className="font-mono-cutive text-[13px] py-5 pr-4">{it.email}</td>
                      <td className="font-display italic text-[17px] py-5 pr-4">{it.primary_focus}</td>
                      <td className="font-mono-cutive text-[12px] py-5 pr-4 text-[#5A5A5A]">{fmt(it.created_at)}</td>
                      <td className="font-mono-cutive text-[12px] py-5 pr-4">
                        {it.email_sent ? (
                          <span style={{ color: "#2E4F3B" }}>SENT</span>
                        ) : (
                          <span className="text-[#5A5A5A]">—</span>
                        )}
                      </td>
                      <td className="py-5 text-right">
                        <button
                          data-testid={`delete-row-${i}`}
                          onClick={() => deleteEntry(it.id)}
                          className="font-mono-cutive text-[11px] tracking-[0.2em] uppercase text-[#5A5A5A] hover:text-[#800606] transition-colors"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
